"use client";

import type { RefObject } from "react";

import { useCallback, useEffect, useRef, useState } from "react";

import { useNativeAudio } from "@/app/_providers/native-audio";
import { getOrCreateMediaElementSource } from "@/app/_ui/components/helpers/mediaElementSource";
import { logger } from "@utilities/logger";

export function useNativeAudioOrbAnalyser(sourceName: string) {
  const { audioRef } = useNativeAudio();

  return useAudioElementOrbAnalyser(audioRef, sourceName);
}

export function useAudioElementOrbAnalyser(audioRef: RefObject<HTMLMediaElement | null>, sourceName: string) {
  const [outputAnalyser, setOutputAnalyser] = useState<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) {
      setOutputAnalyser(null);
      audioContextRef.current = null;
      return;
    }

    try {
      const { ctx, src } = getOrCreateMediaElementSource(audioElement);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.8;

      src.connect(analyser);
      analyser.connect(ctx.destination);

      audioContextRef.current = ctx;
      setOutputAnalyser(analyser);

      return () => {
        audioContextRef.current = null;
        setOutputAnalyser(current => (current === analyser ? null : current));
        src.disconnect(analyser);
        analyser.disconnect();
      };
    } catch (error) {
      audioContextRef.current = null;
      setOutputAnalyser(null);
      logger.warn(`[${sourceName}] Failed to initialize orb audio analyser`, { error });
    }
  }, [audioRef, sourceName]);

  const ensureAudioContext = useCallback(() => {
    const audioContext = audioContextRef.current;
    if (!audioContext || audioContext.state !== "suspended") return;

    void audioContext.resume().catch(error => {
      logger.warn(`[${sourceName}] Failed to resume orb audio context`, { error });
    });
  }, [sourceName]);

  return { ensureAudioContext, outputAnalyser };
}
